import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Label, RequiredMark } from '@/components/ui';
import {
  fundingCloseSchema,
  type FundingCloseValues,
} from '../schemas/fundingFormSchema';

export interface FundingCloseModalProps {
  isOpen: boolean;
  title: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: (values: FundingCloseValues) => void;
  onCancel: () => void;
}

export const FundingCloseModal = ({
  isOpen,
  title,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: FundingCloseModalProps) => {
  const { t } = useTranslation('funding');
  const form = useForm<FundingCloseValues>({
    resolver: zodResolver(fundingCloseSchema),
    defaultValues: { forceCancel: false, cancelReason: '' },
    mode: 'onBlur',
  });

  const forceCancel = form.watch('forceCancel');

  const reasonError = form.formState.errors.cancelReason?.message as string | undefined;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="funding-close-modal" className="max-w-md">
        <DialogTitle>{t('funding.close.title')}</DialogTitle>
        <DialogDescription>
          {t('funding.close.subject', { title })}
        </DialogDescription>

        <form
          onSubmit={form.handleSubmit(onConfirm)}
          className="flex flex-col gap-3"
          noValidate
        >
          <p
            className="rounded-md p-3 text-sm"
            style={{
              background: forceCancel ? 'var(--status-warn-bg)' : 'var(--status-info-bg)',
              color: forceCancel ? 'var(--status-warn-fg)' : 'var(--status-info-fg)',
            }}
          >
            {forceCancel ? t('funding.close.confirmForce') : t('funding.close.confirmAuto')}
          </p>

          <label className="inline-flex items-center gap-2 text-sm text-[color:var(--content-default)]">
            <input
              type="checkbox"
              {...form.register('forceCancel')}
              data-testid="force-cancel-toggle"
            />
            <span>{t('funding.close.forceCancelLabel')}</span>
          </label>

          {forceCancel ? (
            <div className="flex flex-col gap-1">
              <Label htmlFor="close-reason">
                {t('funding.close.cancelReasonLabel')}
                <RequiredMark />
              </Label>
              <textarea
                id="close-reason"
                {...form.register('cancelReason')}
                maxLength={500}
                rows={3}
                placeholder={t('funding.close.cancelReasonPlaceholder')}
                className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
                style={{ borderColor: 'var(--content-muted)' }}
                aria-invalid={Boolean(reasonError)}
              />
              {reasonError ? (
                <span
                  role="alert"
                  className="text-xs"
                  style={{ color: 'var(--signal-danger)' }}
                >
                  {t(reasonError, { defaultValue: reasonError })}
                </span>
              ) : null}
            </div>
          ) : null}

          {errorMessage ? (
            <p
              role="alert"
              aria-live="polite"
              className="rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('funding.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant={forceCancel ? 'danger' : 'cta'}
              size="md"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t('funding.close.submitting')
                : t('funding.close.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
